import { call, put, takeEvery, select, takeLatest } from "redux-saga/effects";
import {
  FetchStatisticsForPeriodAction,
  FetchFilteredStatisticsAction,
  FetchChartsForPeriodAction,
  FetchEventsForPeriodAction,
  FetchEventsByIdAction,
  FetchFilteredEventsAction,
  FetchDiaryEventsAction,
  FetchDetailsForEventAction,
  FetchDocumentForEventAction,
  FetchScoresForEventAction,
  FetchViewsForEventAction,
  addEvents,
  setStatisticsForPeriodLoading,
  setStatisticsForPeriod,
  setStatisticsForPeriodError,
  setFilteredStatisticsLoading,
  setFilteredStatistics,
  setFilteredStatisticsError,
  setChartsForPeriodLoading,
  setChartsForPeriod,
  setChartsForPeriodError,
  setEventsForPeriodLoading,
  setEventsForPeriod,
  setEventsForPeriodError,
  setFilteredEventsLoading,
  setFilteredEvents,
  setFilteredEventsError,
  setDiaryEventsLoading,
  setDiaryEvents,
  setDiaryEventsError,
  setEventDetailsLoading,
  setEventDetails,
  setEventDetailsError,
  setEventDocumentLoading,
  setEventDocument,
  setEventDocumentError,
  setEventScoresLoading,
  setEventScores,
  setEventScoresError,
  setEventViewsLoading,
  setEventViews,
  setEventViewsError,
  FETCH_STATISTICS_FOR_PERIOD,
  FETCH_FILTERED_STATISTICS,
  FETCH_CHARTS_FOR_PERIOD,
  FETCH_EVENTS_FOR_PERIOD,
  FETCH_EVENTS_BY_ID,
  FETCH_FILTERED_EVENTS,
  FETCH_DETAILS_FOR_EVENT,
  FETCH_DOCUMENT_FOR_EVENT,
  FETCH_SCORES_FOR_EVENT,
  FETCH_VIEWS_FOR_EVENT,
  FETCH_DIARY_EVENTS,
} from "../actions/events.actions";
import { eventsApi, Event } from "../../api";
import { IStateType } from "../models/root.interface";
import { IEventState } from "../models/events.interface";
import { addMonths, format, startOfMonth, formatISO } from "date-fns";

const api = {
  getEventStatisticsForPeriod: (
    ...args: Parameters<typeof eventsApi.getEventsStatisticsForPeriod>
  ) => eventsApi.getEventsStatisticsForPeriod(...args),
  getEventsChartsForPeriod: (
    ...args: Parameters<typeof eventsApi.getEventsChartsForPeriod>
  ) => eventsApi.getEventsChartsForPeriod(...args),
  getEventsForPeriod: (
    ...args: Parameters<typeof eventsApi.getEventsForPeriod>
  ) => eventsApi.getEventsForPeriod(...args),
  getDetailsForEvent: (
    ...args: Parameters<typeof eventsApi.getDetailsForEvent>
  ) => eventsApi.getDetailsForEvent(...args),
  getDocumentForEvent: (
    ...args: Parameters<typeof eventsApi.getDocumentForEvent>
  ) => eventsApi.getDocumentForEvent(...args),
  getScoresForEvent: (
    ...args: Parameters<typeof eventsApi.getScoresForEvent>
  ) => eventsApi.getScoresForEvent(...args),
  getViewsForEvent: (
    ...args: Parameters<typeof eventsApi.getViewsForEvent>
  ) => eventsApi.getViewsForEvent(...args),
  getEventsForDateRange: (
    ...args: Parameters<typeof eventsApi.getEventsByDateRange>
  ) => eventsApi.getEventsByDateRange(...args),
  getEventsById: (
    ...args: Parameters<typeof eventsApi.getEventsById>
  ) => eventsApi.getEventsById(...args),
};

function* fetchStatistics(action: FetchStatisticsForPeriodAction) {
  const state: IEventState = yield select((state: IStateType) => state.event);
  const { period } = action;
  if (
    !state.statisticsByPeriod[period] ||
    state.statisticsByPeriod[period] === "error"
  ) {
    // Denote loading
    yield put(setStatisticsForPeriodLoading(period));
    try {
      // Request
      const { data } = yield call(api.getEventStatisticsForPeriod, { period });
      // Store data for period
      yield put(setStatisticsForPeriod(period, data));
    } catch (e) {
      // Denote error
      yield put(setStatisticsForPeriodError(period));
    }
  }
}

function* fetchFilteredStatistics(action: FetchFilteredStatisticsAction) {
  const { period, filters } = action;
  // Denote loading
  yield put(setFilteredStatisticsLoading());
  try {
    // Request
    const { data } = yield call(api.getEventStatisticsForPeriod, {
      period,
      ...filters,
    });
    // Store data for period
    yield put(setFilteredStatistics(data));
  } catch (e) {
    // Denote error
    yield put(setFilteredStatisticsError());
  }
}

function* fetchCharts(action: FetchChartsForPeriodAction) {
  const state: IEventState = yield select((state: IStateType) => state.event);
  const { period } = action;
  if (
    !state.chartsByPeriod[period] ||
    state.chartsByPeriod[period] === "error"
  ) {
    // Denote loading
    yield put(setChartsForPeriodLoading(period));
    try {
      // Request
      const { data } = yield call(api.getEventsChartsForPeriod, { period });
      // Store data for period
      yield put(setChartsForPeriod(period, data));
    } catch (e) {
      // Denote error
      yield put(setChartsForPeriodError(period));
    }
  }
}

function* fetchEventsForPeriod(action: FetchEventsForPeriodAction) {
  const state: IEventState = yield select((state: IStateType) => state.event);
  const { period } = action;
  if (
    !state.eventUidsByPeriod[period] ||
    state.eventUidsByPeriod[period] === "error"
  ) {
    // Denote loading
    yield put(setEventsForPeriodLoading(period));
    try {
      // Request
      const { data } = yield call(api.getEventsForPeriod, { period });
      // Store events
      yield put(addEvents(data));
      // Store event IDs for period
      yield put(setEventsForPeriod(period, data));
    } catch (e) {
      // Denote error
      yield put(setEventsForPeriodError(period));
    }
  }
}

function* fetchFilteredEvents(action: FetchFilteredEventsAction) {
  const { period, filters } = action;
  // Denote loading
  yield put(setFilteredEventsLoading());
  try {
    // Request
    const { data } = yield call(api.getEventsForPeriod, {
      period,
      ...filters
    });
    // Store events
    yield put(addEvents(data));
    // Store event IDs for period
    yield put(setFilteredEvents((data as Event[]).map(x => x.id ?? 0)));
  } catch (e) {
    // Denote error
    yield put(setFilteredEventsError());
  }
}

function* fetchDiaryEvents(action: FetchDiaryEventsAction) {
  const state: IEventState = yield select((state: IStateType) => state.event);
  const key = format(action.date, "yyyy-MM");
  if (!(key in state.diary.eventUids) || state.diary.eventUids[key] === "error") {
    // Denote loading
    yield put(setDiaryEventsLoading(key));
    try {
      // Request
      const lowerBound = startOfMonth(action.date);
      const upperBound = addMonths(lowerBound, 1);
      const { data } = yield call(api.getEventsForDateRange, {
        lowerBound: formatISO(lowerBound),
        upperBound: formatISO(upperBound),
      });
      // Store events
      yield put(addEvents(data));
      // Store event IDs for month
      yield put(
        setDiaryEvents(
          key,
          (data as Event[]).map((x) => x.id ?? 0)
        )
      );
    } catch (e) {
      // Denote error
      yield put(setDiaryEventsError(key));
    }
  }
}

function* fetchEventsById(action: FetchEventsByIdAction) {
  const state: IEventState = yield select((state: IStateType) => state.event);
  const events = Object.values(state.eventsByUid);
  const eventIds = action.eventIds.filter(id => !events.some(x => x?.eventId === id));
  if (eventIds.length !== 0) {
    try {
      // Request
      const { data } = yield call(api.getEventsById, {
        eventIds,
      });
      // Store events
      yield put(addEvents(data));
    } catch (e) {
    }
  }
}

function* fetchDetailsForEvent(action: FetchDetailsForEventAction) {
  const state: IEventState = yield select((state: IStateType) => state.event);
  const { eventId } = action;
  if (
    !(eventId in state.sessionsByEventId) ||
    state.sessionsByEventId[eventId] === "error"
  ) {
    // Denote loading
    yield put(setEventDetailsLoading(eventId));
    try {
      // Request
      const { data } = yield call(api.getDetailsForEvent, {
        eventId,
      });
      // Store presenters
      yield put(setEventDetails(eventId, data));
    } catch (e) {
      // Denote error
      yield put(setEventDetailsError(eventId));
    }
  }
}

function* fetchDocumentForEvent(action: FetchDocumentForEventAction) {
  const state: IEventState = yield select((state: IStateType) => state.event);
  const { eventId, ticketId } = action;
  const eventUid = `${eventId}=${ticketId}`
  if (
    !(eventUid in state.downloadUrlByEventUid) ||
    state.downloadUrlByEventUid[eventUid] === "error"
  ) {
    // Denote loading
    yield put(setEventDocumentLoading(eventId, ticketId));
    try {
      // Request
      const { data } = yield call(api.getDocumentForEvent, {
        eventId,
        ticketId,
      });
      // Store presenters
      yield put(setEventDocument(eventId, ticketId, data));
    } catch (e) {
      // Denote error
      yield put(setEventDocumentError(eventId, ticketId));
    }
  }
}

function* fetchScoresForEvent(action: FetchScoresForEventAction) {
  const state: IEventState = yield select((state: IStateType) => state.event);
  const { eventId, ticketId } = action;
  const eventUid = `${eventId}-${ticketId}`;
  if (
    !(eventUid in state.scoresByEventUid) ||
    state.scoresByEventUid[eventUid] === "error"
  ) {
    // Denote loading
    yield put(setEventScoresLoading(eventUid));
    try {
      // Request
      const { data } = yield call(api.getScoresForEvent, {
        eventId,
        ticketId,
      });
      // Store scores
      yield put(setEventScores(eventUid, data));
    } catch (e) {
      // Denote error
      yield put(setEventScoresError(eventUid));
    }
  }
}

function* fetchViewsForEvent(action: FetchViewsForEventAction) {
  const state: IEventState = yield select((state: IStateType) => state.event);
  const { eventId, ticketId } = action;
  const eventUid = `${eventId}-${ticketId}`;
  if (
    !(eventUid in state.viewsByEventUid) ||
    state.viewsByEventUid[eventUid] === "error"
  ) {
    // Denote loading
    yield put(setEventViewsLoading(eventId, ticketId));
    try {
      // Request
      const { data } = yield call(api.getViewsForEvent, {
        eventId,
        ticketId,
      });
      // Store presenters
      yield put(setEventViews(eventId, ticketId, data));
    } catch (e) {
      // Denote error
      yield put(setEventViewsError(eventId, ticketId));
    }
  }
}

function* eventsSaga() {
  yield takeEvery(FETCH_STATISTICS_FOR_PERIOD, fetchStatistics);
  yield takeEvery(FETCH_CHARTS_FOR_PERIOD, fetchCharts);
  yield takeEvery(FETCH_EVENTS_FOR_PERIOD, fetchEventsForPeriod);
  yield takeEvery(FETCH_DIARY_EVENTS, fetchDiaryEvents);
  yield takeEvery(FETCH_EVENTS_BY_ID, fetchEventsById);
  yield takeEvery(FETCH_DETAILS_FOR_EVENT, fetchDetailsForEvent);
  yield takeEvery(FETCH_DOCUMENT_FOR_EVENT, fetchDocumentForEvent);
  yield takeEvery(FETCH_SCORES_FOR_EVENT, fetchScoresForEvent);
  yield takeEvery(FETCH_VIEWS_FOR_EVENT, fetchViewsForEvent);
  yield takeLatest(FETCH_FILTERED_STATISTICS, fetchFilteredStatistics);
  yield takeLatest(FETCH_FILTERED_EVENTS, fetchFilteredEvents);
}

export default eventsSaga;
